import { format, parseISO } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";

import type { AdminTicketFiltersDto } from "@/api/types";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { FormCheckboxGroup } from "@/components/Form/FormCheckbox";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { Drawer, type DrawerBaseProps } from "@/design-system/Drawer";
import { FormField } from "@/design-system/FormField";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { ticketRatingValues, ticketVisibilityValues } from "@/queries/tickets/constants";
import { useTranslation } from "@/translations";

import { TicketAssigneeFilter, type TicketAssigneeFilterMode } from "./TicketAssigneeFilter";

export type TicketsFilterValues = {
  assigneeIds?: string[];
  assigneeMode?: TicketAssigneeFilterMode;
  ratings?: number[];
  statusIds?: string[];
  categoryIds?: string[];
  visibility?: "private" | "collective";
  addressTypes?: string[];
  isUnreadOnly?: boolean;
  closedFrom?: string;
  closedTo?: string;
  ratedFrom?: string;
  ratedTo?: string;
};

type TicketsFilterDrawerProps = DrawerBaseProps & {
  filter: TicketsFilterValues;
  filterOptions: AdminTicketFiltersDto;
  onUpdate: (filter: TicketsFilterValues) => void;
};

export function TicketsFilterDrawer({
  filter,
  onUpdate,
  filterOptions,
  isOpened,
  onOpenChange,
}: TicketsFilterDrawerProps): React.ReactNode {
  const [currFilter, setCurrFilter] = useState<TicketsFilterValues>(filter);
  const { t } = useTranslation();
  const sessionUser = useSessionUser();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrFilter(filter);
  }, [filter]);

  const handleApplyFilter = () => {
    onUpdate(currFilter);
  };

  const handleClearFilter = () => {
    onUpdate({
      assigneeIds: undefined,
      assigneeMode: undefined,
      ratings: undefined,
      statusIds: undefined,
      categoryIds: undefined,
      visibility: undefined,
      addressTypes: undefined,
      isUnreadOnly: undefined,
      closedFrom: undefined,
      closedTo: undefined,
      ratedFrom: undefined,
      ratedTo: undefined,
    });
  };

  return (
    <Drawer.Root
      title={t("page.tickets.filter-drawer.title")}
      closeLabel={t("common.action.close")}
      onOpenChange={onOpenChange}
      isOpened={isOpened}
    >
      <Drawer.Body>
        <Stack gap="md">
          <FormField label={t("page.tickets.filter-drawer.assignee.label")}>
            <TicketAssigneeFilter
              items={filterOptions.assignees}
              selectedIds={currFilter.assigneeIds}
              mode={currFilter.assigneeMode}
              onChange={(assigneeIds) => setCurrFilter({ ...currFilter, assigneeIds })}
              onModeChange={(assigneeMode) => setCurrFilter({ ...currFilter, assigneeMode })}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.status.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={filterOptions.statuses}
              selected={filterOptions.statuses.filter((x) => currFilter.statusIds?.includes(x.id))}
              onChange={(s) => setCurrFilter({ ...currFilter, statusIds: s.map(({ id }) => id) })}
              placeholder={t("page.tickets.filter-drawer.status.placeholder")}
              renderOption={(x) => <TicketStatus labelColor={x.color} description={x.name} variant="filled" />}
              keySelector={(x) => x.id}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.category.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={filterOptions.categories}
              selected={filterOptions.categories.filter((x) => currFilter.categoryIds?.includes(x.id))}
              onChange={(c) => setCurrFilter({ ...currFilter, categoryIds: c.map(({ id }) => id) })}
              placeholder={t("page.tickets.filter-drawer.category.placeholder")}
              renderOption={(x) => x.name}
              keySelector={(x) => x.id}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.rating.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={ticketRatingValues}
              selected={ticketRatingValues.filter((x) => currFilter.ratings?.includes(x))}
              onChange={(x) => setCurrFilter({ ...currFilter, ratings: x })}
              placeholder={t("page.tickets.filter-drawer.rating.placeholder")}
              renderOption={(x) => {
                if (x === -1) {
                  return t("page.tickets.filter-drawer.rating.not-rated");
                }

                return <RatingBadge rating={x} />;
              }}
              keySelector={(x) => x}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.private-collective.label")}>
            <Select
              items={ticketVisibilityValues}
              selected={currFilter.visibility}
              emptyItem={t("page.tickets.filter-drawer.private-collective.placeholder")}
              onChange={(x) => setCurrFilter({ ...currFilter, visibility: x })}
              renderOption={(x) => {
                switch (x) {
                  case "private":
                    return t("page.tickets.filter-drawer.private-collective.private");
                  case "collective":
                    return t("page.tickets.filter-drawer.private-collective.collective");
                }
              }}
              keySelector={(x) => x}
            />
          </FormField>
          {sessionUser.project.type === "addressBased" && (
            <FormField label={t("page.tickets.filter-drawer.address-type.label")}>
              <CheckboxMultiSelect
                renderMoreSelected={(count) => t("common.label.x-more", { count })}
                items={filterOptions.addressTypes}
                selected={filterOptions.addressTypes.filter((x) => currFilter.addressTypes?.includes(x))}
                onChange={(x) => setCurrFilter({ ...currFilter, addressTypes: x })}
                placeholder={t("page.tickets.filter-drawer.address-type.placeholder")}
                renderOption={(x) => x}
                keySelector={(x) => x}
              />
            </FormField>
          )}
          <FormField label={t("page.tickets.filter-drawer.closed-from-date.label")}>
            <DatePicker
              placeholder={t("page.tickets.filter-drawer.closed-from-date.placeholder")}
              value={currFilter.closedFrom ? parseISO(currFilter.closedFrom) : undefined}
              onChange={(value) =>
                setCurrFilter({ ...currFilter, closedFrom: value ? format(value, "yyyy-MM-dd") : undefined })
              }
              max={currFilter.closedTo ? parseISO(currFilter.closedTo) : undefined}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.closed-to-date.label")}>
            <DatePicker
              placeholder={t("page.tickets.filter-drawer.closed-to-date.placeholder")}
              value={currFilter.closedTo ? parseISO(currFilter.closedTo) : undefined}
              onChange={(value) =>
                setCurrFilter({ ...currFilter, closedTo: value ? format(value, "yyyy-MM-dd") : undefined })
              }
              min={currFilter.closedFrom ? parseISO(currFilter.closedFrom) : undefined}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.rated-from-date.label")}>
            <DatePicker
              placeholder={t("page.tickets.filter-drawer.rated-from-date.placeholder")}
              value={currFilter.ratedFrom ? parseISO(currFilter.ratedFrom) : undefined}
              onChange={(value) =>
                setCurrFilter({ ...currFilter, ratedFrom: value ? format(value, "yyyy-MM-dd") : undefined })
              }
              max={currFilter.ratedTo ? parseISO(currFilter.ratedTo) : undefined}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.rated-to-date.label")}>
            <DatePicker
              placeholder={t("page.tickets.filter-drawer.rated-to-date.placeholder")}
              value={currFilter.ratedTo ? parseISO(currFilter.ratedTo) : undefined}
              onChange={(value) =>
                setCurrFilter({ ...currFilter, ratedTo: value ? format(value, "yyyy-MM-dd") : undefined })
              }
              min={currFilter.ratedFrom ? parseISO(currFilter.ratedFrom) : undefined}
            />
          </FormField>
          <FormCheckboxGroup layout="vertical">
            <Checkbox
              label={t("page.tickets.filter-drawer.unread.label")}
              name="tickets_filter_unread"
              checked={currFilter.isUnreadOnly}
              onChange={(e) => setCurrFilter({ ...currFilter, isUnreadOnly: e.target.checked })}
            />
          </FormCheckboxGroup>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClearFilter}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button onClick={handleApplyFilter}>{t("common.action.apply")}</Button>
        </Drawer.Close>
      </Drawer.Actions>
    </Drawer.Root>
  );
}
